/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Swatch } from '@/components/blocks/Swatch';
import {
	facetNavigationFacetEntryFormControlLabelSX,
	facetNavigationFacetEntrySwatchSX,
} from '@/components/content/FacetNavigation/styles/facetEntry/formControlLabel';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { ProductFacetEntry } from '@/data/types/Product';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { FC, useCallback, useContext } from 'react';

type Props = {
	entry: ProductFacetEntry;
};

export const FacetNavigationEntry: FC<Props> = ({ entry }) => {
	const { selectedFacet, onFacetSelectChange } = useContext(ContentContext) as ReturnType<
		typeof useFacetNavigation
	>;
	const onChange = useCallback(
		(event: any) => onFacetSelectChange(event, entry),
		[entry, onFacetSelectChange]
	);
	const productFilterNLS = useLocalization('ProductFilter');
	return entry.image ? (
		<Swatch
			aria-label={entry.label}
			image={entry.image}
			onClick={onChange}
			size="large"
			title={entry.label}
			selected={selectedFacet !== undefined ? selectedFacet.indexOf(entry.value) > -1 : undefined}
			data-testid={`product-filter-${entry.value.toLowerCase()}-swatch`}
			id={`product-filter-${entry.value.toLowerCase()}-swatch`}
			sx={facetNavigationFacetEntrySwatchSX}
		/>
	) : (
		<FormControlLabel
			sx={facetNavigationFacetEntryFormControlLabelSX}
			control={
				<Checkbox
					checked={
						selectedFacet !== undefined ? selectedFacet.indexOf(entry.value) > -1 : undefined
					}
					onChange={onChange}
					id={`checkbox-${entry.label}`}
				/>
			}
				label={<Stack direction="row" justifyContent="space-between" width="100%">
					<Typography aria-hidden="true">{entry.label}</Typography>
					<Typography aria-hidden="true"> ({entry.count})</Typography>
				</Stack>}
			id={`${entry.label} (${entry.count})`}
			data-testid={`${entry.label} (${entry.count})`}
			aria-label={`${entry.label} (${entry.count} ${productFilterNLS.Labels.artikelen.t()})`}
			className='facetbutton'  /* Added to track filter clicks in GTM  */
			htmlFor={`checkbox-${entry.label}`}
		/>
	);
};
